import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Grid, MenuItem,
  CircularProgress, Alert, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, IconButton, Tooltip, Chip, Stack, InputAdornment,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import {
  Add, Payments as PaymentsIcon, Receipt,
  Delete, Edit, ChevronLeft, ChevronRight, AccountBalance,
  Savings, TrendingUp, TrendingDown, Settings, Close, SwapHoriz,
  InboxOutlined, ReceiptLongOutlined, PaymentsOutlined,
  OpenInFull, Search, ReceiptLong,
} from '@mui/icons-material';
import axios from 'axios';
import Swal from 'sweetalert2';
import {
  cashbookSummaryURL, bankAccountsURL,
  weeklyCashURL, weeklyCashDeleteURL,
  bankDepositsURL, bankDepositsDeleteURL,
  paymentVouchersURL, paymentVouchersDeleteURL,
  accountingPeriodsURL, accountingPeriodsAddURL,
  accountingPeriodsToggleURL, accountingPeriodsDeleteURL,
} from '../BaseURL';

import RecordIncomeDrawer   from './RecordIncomeDrawer';
import RecordPaymentDrawer  from './RecordPaymentDrawer';
import CashToBankDrawer     from './CashToBankDrawer';
import PaymentVoucherDialog from './PaymentVoucherDialog';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('church_token')}` });
const swalTop = { customClass: { container: 'swal-on-top' } };
const fmt = (v) => Number(v || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 });
const shortDate = (d) => new Date(d).toLocaleDateString('en-GH', { day: '2-digit', month: 'short' });
const getUser = () => { try { return JSON.parse(localStorage.getItem('church_user')); } catch { return null; } };

const MONTHS = ['January','February','March','April','May','June',
                'July','August','September','October','November','December'];

// Each of the three columns is an explicit flex box with a definite height on
// md+, so the card inside it has something real to size against — relying on
// `align-items: stretch` alone left the card unbounded and its rows clipped.
const COLUMN_SX = { display: 'flex', minHeight: 0, height: { md: '100%' } };

const boundsFor = (monthName, year) => {
  const idx = MONTHS.indexOf(monthName);
  const first = new Date(Date.UTC(year, idx, 1)).toISOString().slice(0, 10);
  const last  = new Date(Date.UTC(year, idx + 1, 0)).toISOString().slice(0, 10);
  return { first, last };
};

export default function CashBook() {
  const now  = new Date();
  const user = getUser();
  const isAdmin = ['superadmin', 'admin'].includes(user?.role);
  const canWriteIncome  = isAdmin || user?.permissions?.record_income;
  const canWritePayment = isAdmin || user?.permissions?.payments;

  const [selMonth, setSelMonth] = useState(MONTHS[now.getMonth()]);
  const [selYear,  setSelYear]  = useState(now.getFullYear());
  const monthYear = `${selMonth}/${selYear}`;
  const { first: periodFirstDay, last: periodLastDay } = useMemo(
    () => boundsFor(selMonth, selYear), [selMonth, selYear]
  );

  const [periods, setPeriods] = useState([]);
  const activeYears = useMemo(
    () => periods.filter(p => p.is_active).map(p => p.year).sort((a, b) => b - a),
    [periods]
  );

  const [summary,  setSummary]  = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [receipts, setReceipts] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  const [incomeOpen,  setIncomeOpen]  = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [depositOpen, setDepositOpen] = useState(false);
  const [yearsOpen,   setYearsOpen]   = useState(false);

  // Which column is open in its full-screen table view: 'receipts' | 'bank' | 'payments'
  const [expanded, setExpanded] = useState(null);

  // Payment whose voucher is being raised, and whether to open it combined.
  const [voucherFor, setVoucherFor]     = useState(null);
  const [voucherScope, setVoucherScope] = useState(null);

  const raiseVoucher = (p, scope = null) => { setVoucherScope(scope); setVoucherFor(p); };

  // Cheque number currently isolated in the Payments full view.
  const [chequeFilter, setChequeFilter] = useState('');

  // Entries pre-loaded into a drawer for editing when a table row's edit icon is clicked.
  const [incomeEdit,  setIncomeEdit]  = useState(null);
  const [paymentEdit, setPaymentEdit] = useState(null);
  const [depositEdit, setDepositEdit] = useState(null);

  const openReceiptEdit = (r) => { setIncomeEdit(r);  setIncomeOpen(true); };
  const openPaymentEdit = (p) => { setPaymentEdit(p); setPaymentOpen(true); };
  const openDepositEdit = (d) => { setDepositEdit(d); setDepositOpen(true); };

  const loadPeriods = useCallback(() => {
    axios.get(accountingPeriodsURL, { headers: headers() })
      .then(r => setPeriods(r.data.data || []))
      .catch(() => {});
  }, []);

  const loadData = useCallback(() => {
    setLoading(true); setError('');
    Promise.all([
      axios.get(`${cashbookSummaryURL}?month_year=${monthYear}`, { headers: headers() }),
      axios.get(bankAccountsURL, { headers: headers() }),
      axios.get(`${weeklyCashURL}?month_year=${monthYear}`,      { headers: headers() }),
      axios.get(`${bankDepositsURL}?month_year=${monthYear}`,    { headers: headers() }),
      axios.get(`${paymentVouchersURL}?month_year=${monthYear}`, { headers: headers() }),
    ]).then(([s, a, r, d, p]) => {
      setSummary(s.data);
      setAccounts(a.data.data || []);
      setReceipts(r.data.data || []);
      setDeposits(d.data.data || []);
      setPayments(p.data.data || []);
    }).catch(err => setError(err.response?.data?.message || err.message))
      .finally(() => setLoading(false));
  }, [monthYear]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);
  useEffect(() => { loadData(); }, [loadData]);

  const stepMonth = (dir) => {
    const idx = MONTHS.indexOf(selMonth) + dir;
    if (idx < 0) { setSelMonth(MONTHS[11]); setSelYear(y => y - 1); }
    else if (idx > 11) { setSelMonth(MONTHS[0]); setSelYear(y => y + 1); }
    else setSelMonth(MONTHS[idx]);
  };

  const confirmDelete = (title, text) => Swal.fire({
    ...swalTop, title, text, icon: 'warning',
    showCancelButton: true, confirmButtonColor: '#B91C1C', confirmButtonText: 'Delete',
  });
  const deleteReceipt = async (r) => {
    if (!(await confirmDelete('Delete income entry?', `${r.income_type_name} — GH₵ ${fmt(r.amount)}`)).isConfirmed) return;
    await axios.post(weeklyCashDeleteURL, { id: r.id }, { headers: headers() }); loadData();
  };
  const deleteDeposit = async (d) => {
    if (!(await confirmDelete('Delete deposit?', `${d.account_name} — GH₵ ${fmt(d.amount)}`)).isConfirmed) return;
    await axios.post(bankDepositsDeleteURL, { id: d.id }, { headers: headers() }); loadData();
  };
  const deletePayment = async (p) => {
    if (!(await confirmDelete(`Delete ${p.pv_number}?`, `GH₵ ${fmt(p.amount)}`)).isConfirmed) return;
    await axios.post(paymentVouchersDeleteURL, { id: p.id }, { headers: headers() }); loadData();
  };

  // Cheques used this month, each with how many vouchers it covers. A cheque
  // that paid several expenses can be printed as one combined voucher or as
  // separate ones, so we surface the count up front.
  const chequeGroups = useMemo(() => {
    const map = new Map();
    for (const p of payments) {
      const no = String(p.cheque_no || '').trim();
      if (!no) continue;
      if (!map.has(no)) map.set(no, { no, count: 0, total: 0, accounts: new Set() });
      const g = map.get(no);
      g.count += 1;
      g.total += Number(p.amount || 0);
      if (p.account_name) g.accounts.add(p.account_name);
    }
    return [...map.values()].sort((a, b) => b.count - a.count || a.no.localeCompare(b.no));
  }, [payments]);

  const activeCheque   = chequeGroups.find(g => g.no === chequeFilter) || null;
  const chequePayments = activeCheque
    ? payments.filter(p => String(p.cheque_no || '').trim() === activeCheque.no)
    : payments;

  const cashInHand      = Number(summary?.cash_in_hand || 0);
  const totalBank       = Number(summary?.total_bank_balance || 0);
  const monthIncome     = Number(summary?.cash_income_this_month || 0);
  const monthExpense    = payments.reduce((s, p) => s + Number(p.amount), 0);
  const closingPosition = cashInHand + totalBank;

  const [newYear, setNewYear] = useState('');
  const addYear = async () => {
    const y = parseInt(newYear, 10);
    if (!y || y < 2000 || y > 2100) {
      Swal.fire({ ...swalTop, icon: 'warning', title: 'Enter a valid year' });
      return;
    }
    await axios.post(accountingPeriodsAddURL, { year: y }, { headers: headers() });
    setNewYear(''); loadPeriods();
  };
  const toggleYear = async (p) => {
    await axios.post(accountingPeriodsToggleURL, { id: p.id, is_active: p.is_active ? 0 : 1 }, { headers: headers() });
    loadPeriods();
  };
  const removeYear = (p) => Swal.fire({
    ...swalTop, title: `Remove year ${p.year}?`, icon: 'warning',
    showCancelButton: true, confirmButtonColor: '#B91C1C', confirmButtonText: 'Remove',
  }).then(async r => {
    if (r.isConfirmed) {
      await axios.post(accountingPeriodsDeleteURL, { id: p.id }, { headers: headers() });
      loadPeriods();
    }
  });

  return (
    // On md+ the page is pinned to the viewport and each column scrolls on its
    // own. Below md the columns stack, so we let the page scroll normally.
    <Box sx={{
      height: { md: '100%' },
      display: 'flex', flexDirection: 'column', minHeight: 0,
    }}>
      {/* Header */}
      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" color="secondary.main" fontWeight={700} sx={{ letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            Overview
          </Typography>
          <Typography variant="h4" fontWeight={800} sx={{ letterSpacing: '-0.02em', mt: 0.5 }}>
            Cash Book
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Receipts, bank movements and payments — the full monthly picture in one view.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => stepMonth(-1)} size="small"><ChevronLeft /></IconButton>
          <TextField select size="small" value={selMonth}
            onChange={e => setSelMonth(e.target.value)} sx={{ width: 140 }}>
            {MONTHS.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={selYear}
            onChange={e => setSelYear(Number(e.target.value))} sx={{ width: 110 }}>
            {(activeYears.length ? activeYears : [now.getFullYear()]).map(y => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </TextField>
          <IconButton onClick={() => stepMonth(1)} size="small"><ChevronRight /></IconButton>
          {isAdmin && (
            <Tooltip title="Manage accounting years">
              <IconButton onClick={() => setYearsOpen(true)} size="small"><Settings /></IconButton>
            </Tooltip>
          )}
        </Stack>
      </Box>

      {/* Actions */}
      <Stack direction="row" spacing={1} sx={{ mb: 3, flexWrap: 'wrap', flexShrink: 0 }} useFlexGap>
        {canWriteIncome && (
          <Button variant="contained" startIcon={<Add />} onClick={() => setIncomeOpen(true)} sx={{ px: 3 }}>
            Record Income
          </Button>
        )}
        {canWritePayment && (
          <Button variant="contained" color="secondary" startIcon={<Add />} onClick={() => setPaymentOpen(true)} sx={{ px: 3 }}>
            Record Payment
          </Button>
        )}
        {canWriteIncome && (
          <Button variant="outlined" startIcon={<Add />} onClick={() => setDepositOpen(true)}>
            Cash → Bank
          </Button>
        )}
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2, flexShrink: 0 }} onClose={() => setError('')}>{error}</Alert>}

      {/* KPI strip */}
      <Grid container spacing={2} sx={{ mb: 3, flexShrink: 0 }}>
        {[
          { label: 'Cash in hand',   value: cashInHand,   icon: <Savings />,        color: 'primary.main' },
          { label: 'Bank balance',   value: totalBank,    icon: <AccountBalance />, color: 'secondary.main' },
          { label: 'Month income',   value: monthIncome,  icon: <TrendingUp />,     color: 'success.main' },
          { label: 'Month expense',  value: monthExpense, icon: <TrendingDown />,   color: 'error.main' },
        ].map(s => (
          <Grid item xs={12} sm={6} md={3} key={s.label}>
            <Card sx={{
              height: '100%',
              transition: 'transform 120ms, box-shadow 120ms',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 6px 20px rgba(15,23,42,0.08)',
              },
            }}>
              <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  width: 44, height: 44, borderRadius: 1.5,
                  bgcolor: s.color, color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(11,37,69,0.15)',
                }}>
                  {s.icon}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, fontSize: 10 }}>
                    {s.label}
                  </Typography>
                  <Typography variant="h5" fontWeight={800} noWrap sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    GH₵ {fmt(s.value)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loading && !summary ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <>
          <Grid container spacing={2} alignItems="stretch" sx={{
            mb: 3,
            // Takes whatever height is left over; each column scrolls internally.
            // minHeight keeps the columns usable on very short screens (the page
            // itself scrolls a little in that case rather than crushing them).
            // No overflow:hidden here — if a child ever fails to bound itself we
            // want the page to scroll, never to silently clip records away.
            flex: { md: 1 },
            minHeight: { md: 420 },
          }}>
            {/* ── Column 1: Receipts ── */}
            <Grid item xs={12} md={4} sx={COLUMN_SX}>
              <SectionCard
                icon={<Receipt />}
                iconBg="success.main"
                title="Receipts"
                subtitle={`Income for ${monthYear}`}
                total={monthIncome}
                totalLabel="Received"
                totalColor="success.main"
                count={receipts.length}
                onExpand={() => setExpanded('receipts')}
              >
                <SubHeader label="Entries" count={receipts.length} />
                {receipts.length === 0 ? (
                  <EmptyState
                    icon={<ReceiptLongOutlined />}
                    title="No income yet"
                    hint={canWriteIncome ? 'Click "Record Income" to add the first entry for this month.' : undefined}
                  />
                ) : (
                  <Box>
                    {receipts.map(r => (
                      <FeedRow
                        key={r.id}
                        primary={r.income_type_name}
                        secondary={`${shortDate(r.income_date)} · ${r.income_category || '—'} · ${r.payment_method}`}
                        tertiary={r.narration}
                        amount={r.amount}
                        amountColor="success.main"
                        onEdit={canWriteIncome ? () => openReceiptEdit(r) : undefined}
                        onDelete={canWriteIncome ? () => deleteReceipt(r) : undefined}
                      />
                    ))}
                  </Box>
                )}
              </SectionCard>
            </Grid>

            {/* ── Column 2: Cash & Bank Balances ── */}
            <Grid item xs={12} md={4} sx={COLUMN_SX}>
              <SectionCard
                icon={<AccountBalance />}
                iconBg="primary.main"
                title="Cash & Bank"
                subtitle="Balances at close"
                total={cashInHand + totalBank}
                totalLabel="Total"
                totalColor="primary.main"
                onExpand={() => setExpanded('bank')}
              >
                <SubHeader label="Account" count={(summary?.bank_balances?.length || 0) + 1} />

                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {/* Cash-in-hand row — gold left accent */}
                      <TableRow hover sx={{
                        bgcolor: 'rgba(200,146,17,0.04)',
                        '& td': { borderLeft: '3px solid transparent' },
                        '& td:first-of-type': { borderLeft: '3px solid', borderColor: 'secondary.main' },
                      }}>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1.25}>
                            <Box sx={{
                              width: 28, height: 28, borderRadius: 1,
                              bgcolor: 'rgba(200,146,17,0.15)',
                              color: 'secondary.main',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                            }}>
                              <Savings sx={{ fontSize: 16 }} />
                            </Box>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>Cash in Hand</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                B/f {fmt(summary?.cash_bfwd)} · +{fmt(summary?.cash_income_this_month)} · −{fmt(summary?.cash_deposited_this_month)}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: 800,
                          color: cashInHand >= 0 ? 'primary.main' : 'error.main',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {fmt(cashInHand)}
                        </TableCell>
                      </TableRow>

                      {/* Per-bank rows */}
                      {(summary?.bank_balances || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={2} sx={{ py: 3, border: 0 }}>
                            <EmptyState
                              icon={<AccountBalance />}
                              title="No bank accounts"
                              hint="Add one under Setup → Bank Accounts."
                            />
                          </TableCell>
                        </TableRow>
                      ) : (summary?.bank_balances || []).map(b => (
                        <TableRow key={b.id} hover>
                          <TableCell>
                            <Box display="flex" alignItems="center" gap={1.25}>
                              <Box sx={{
                                width: 28, height: 28, borderRadius: 1,
                                bgcolor: 'rgba(11,37,69,0.08)',
                                color: 'primary.main',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                              }}>
                                <AccountBalance sx={{ fontSize: 16 }} />
                              </Box>
                              <Box>
                                <Typography variant="body2" fontWeight={600}>{b.name}</Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  B/f {fmt(b.bfwd)} · +{fmt(b.total_deposit)} · −{fmt(b.total_payment)}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{
                            fontWeight: 700,
                            color: Number(b.balance) >= 0 ? 'primary.main' : 'error.main',
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                            {fmt(b.balance)}
                          </TableCell>
                        </TableRow>
                      ))}

                      <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                        <TableCell sx={{ fontWeight: 800, letterSpacing: '0.02em' }}>Total (Cash + Bank)</TableCell>
                        <TableCell align="right" sx={{
                          fontWeight: 800, color: 'primary.main',
                          fontVariantNumeric: 'tabular-nums',
                        }}>
                          {fmt(cashInHand + totalBank)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>

                {(() => {
                  // Only user-initiated cash-to-bank deposits are editable here.
                  // Bank-to-bank deposits are owned by their payment voucher.
                  const cashDeps = deposits.filter(
                    d => (d.narration || '').trim().toUpperCase() !== 'BANK TO BANK'
                  );
                  return (
                    <>
                      <SubHeader label="Cash → Bank Deposits" count={cashDeps.length} />
                      {cashDeps.length === 0 ? (
                        <EmptyState
                          icon={<SwapHoriz />}
                          title="No deposits this month"
                          hint={canWriteIncome ? 'Move cash into a bank with "Cash → Bank".' : undefined}
                        />
                      ) : (
                        <Box>
                          {cashDeps.map(d => (
                            <Box key={d.id}
                              sx={{
                                display: 'flex', alignItems: 'center', gap: 1,
                                px: 2, py: 0.9,
                                borderBottom: '1px solid #F1F5F9',
                                '&:last-child': { borderBottom: 'none' },
                                '&:hover': { bgcolor: '#FAFBFC' },
                              }}>
                              <SwapHoriz sx={{ fontSize: 16, color: 'text.disabled' }} />
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{d.account_name}</Typography>
                                <Typography variant="caption" color="text.secondary">{shortDate(d.deposit_date)}</Typography>
                              </Box>
                              <Typography variant="body2" fontWeight={700} color="primary.main"
                                sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                                GH₵ {fmt(d.amount)}
                              </Typography>
                              {canWriteIncome && (
                                <Tooltip title="Edit">
                                  <IconButton size="small" onClick={() => openDepositEdit(d)} sx={{ p: 0.3 }}>
                                    <Edit sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                              {canWriteIncome && (
                                <Tooltip title="Delete">
                                  <IconButton size="small" color="error" onClick={() => deleteDeposit(d)} sx={{ p: 0.3 }}>
                                    <Delete sx={{ fontSize: 14 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Box>
                          ))}
                        </Box>
                      )}
                    </>
                  );
                })()}
              </SectionCard>
            </Grid>

            {/* ── Column 3: Payments ── */}
            <Grid item xs={12} md={4} sx={COLUMN_SX}>
              <SectionCard
                icon={<PaymentsIcon />}
                iconBg="error.main"
                title="Payments"
                subtitle={`Vouchers for ${monthYear}`}
                total={monthExpense}
                totalLabel="Paid"
                totalColor="error.main"
                count={payments.length}
                onExpand={() => setExpanded('payments')}
              >
                <SubHeader label="By Account" count={(summary?.payment_totals || []).length} />
                {(summary?.payment_totals || []).length === 0 ? (
                  <EmptyState
                    icon={<PaymentsOutlined />}
                    title="No payments this month"
                    hint={canWritePayment ? 'Click "Record Payment" to add one.' : undefined}
                  />
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {(summary?.payment_totals || []).map(pt => (
                          <TableRow key={pt.account_name || 'unknown'} hover>
                            <TableCell>
                              <Box display="flex" alignItems="center" gap={1.25}>
                                <Box sx={{
                                  width: 28, height: 28, borderRadius: 1,
                                  bgcolor: 'rgba(185,28,28,0.08)',
                                  color: 'error.main',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <AccountBalance sx={{ fontSize: 16 }} />
                                </Box>
                                <Typography variant="body2" fontWeight={600}>{pt.account_name || '—'}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{
                              fontWeight: 700, color: 'error.main',
                              fontVariantNumeric: 'tabular-nums',
                            }}>
                              {fmt(pt.total)}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                          <TableCell sx={{ fontWeight: 800, letterSpacing: '0.02em' }}>Total Paid</TableCell>
                          <TableCell align="right" sx={{
                            fontWeight: 800, color: 'error.main',
                            fontVariantNumeric: 'tabular-nums',
                          }}>
                            {fmt((summary?.payment_totals || []).reduce((a, p) => a + Number(p.total), 0))}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}

                <SubHeader label="Vouchers" count={payments.length} />
                {payments.length === 0 ? (
                  <EmptyState
                    icon={<InboxOutlined />}
                    title="No vouchers yet"
                    hint={canWritePayment ? 'Payment vouchers you record will appear here.' : undefined}
                  />
                ) : (
                  <Box>
                    {payments.map(p => (
                      <FeedRow
                        key={p.id}
                        primary={p.expense_type || (p.is_bank_to_bank ? 'Bank Transfer' : '—')}
                        secondary={`${shortDate(p.payment_date)} · ${p.is_bank_to_bank ? 'BANK-TO-BANK' : (p.expense_category || '—')} · ${p.account_name || '—'}`}
                        tertiary={p.description}
                        amount={p.amount}
                        amountColor="error.main"
                        onVoucher={() => raiseVoucher(p)}
                        onEdit={canWritePayment ? () => openPaymentEdit(p) : undefined}
                        onDelete={canWritePayment ? () => deletePayment(p) : undefined}
                      />
                    ))}
                  </Box>
                )}
              </SectionCard>
            </Grid>
          </Grid>

          {/* Closing position banner */}
          <Card sx={{
            background: 'linear-gradient(120deg, #0B2545 0%, #13315C 60%, #1E4577 100%)',
            color: '#fff',
            position: 'relative',
            overflow: 'hidden',
            flexShrink: 0,
            '&::before': {
              content: '""',
              position: 'absolute', top: -60, right: -60,
              width: 220, height: 220, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(200,146,17,0.2) 0%, transparent 70%)',
            },
          }}>
            <CardContent sx={{ py: '24px !important', position: 'relative' }}>
              <Grid container alignItems="center" spacing={2}>
                <Grid item xs={12} md={7}>
                  <Typography variant="caption" sx={{ color: 'secondary.light', letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700 }}>
                    Closing Position · {monthYear}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mt: 0.75, fontVariantNumeric: 'tabular-nums' }}>
                    Cash in hand <strong>GH₵ {fmt(cashInHand)}</strong> &nbsp;+&nbsp; Bank balance <strong>GH₵ {fmt(totalBank)}</strong>
                  </Typography>
                </Grid>
                <Grid item xs={12} md={5} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <Typography variant="caption" sx={{
                    display: 'block',
                    color: 'rgba(255,255,255,0.6)',
                    textTransform: 'uppercase', letterSpacing: '0.14em', fontWeight: 700, fontSize: 10,
                  }}>
                    Total
                  </Typography>
                  <Typography variant="h3" fontWeight={800} sx={{
                    color: 'secondary.light', lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    GH₵ {fmt(closingPosition)}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}

      {/* Drawers */}
      <RecordIncomeDrawer
        open={incomeOpen}
        onClose={() => { setIncomeOpen(false); setIncomeEdit(null); }}
        initialEntry={incomeEdit}
        monthYear={monthYear} periodFirstDay={periodFirstDay} periodLastDay={periodLastDay}
        onSaved={loadData}
      />
      <RecordPaymentDrawer
        open={paymentOpen}
        onClose={() => { setPaymentOpen(false); setPaymentEdit(null); }}
        initialEntry={paymentEdit}
        monthYear={monthYear} periodFirstDay={periodFirstDay} periodLastDay={periodLastDay}
        accounts={accounts} bankBalances={summary?.bank_balances || []} cashInHand={cashInHand}
        onSaved={loadData}
      />
      <CashToBankDrawer
        open={depositOpen}
        onClose={() => { setDepositOpen(false); setDepositEdit(null); }}
        initialEntry={depositEdit}
        monthYear={monthYear} periodFirstDay={periodFirstDay} periodLastDay={periodLastDay}
        accounts={accounts} cashInHand={cashInHand}
        onSaved={loadData}
      />

      {/* ── Full-view dialogs, one per column ── */}
      <RecordsDialog
        open={expanded === 'receipts'}
        onClose={() => setExpanded(null)}
        icon={<Receipt />} iconBg="success.main"
        title="Receipts" subtitle={`All income recorded for ${monthYear}`}
        total={monthIncome} totalLabel="Received" totalColor="success.main"
        rows={receipts}
        searchOf={r => [r.income_type_name, r.income_category, r.narration,
                        r.payment_method, r.cheque_no, r.receipt_no].join(' ')}
        emptyIcon={<ReceiptLongOutlined />}
        emptyTitle="No income yet"
        emptyHint={canWriteIncome ? 'Click "Record Income" to add the first entry for this month.' : undefined}
        columns={[
          { label: 'Date', width: 96, cellSx: { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
            render: r => shortDate(r.income_date) },
          { label: 'Income Type', cellSx: { fontWeight: 600 },
            render: r => r.income_type_name || '—' },
          { label: 'Category', render: r => r.income_category || '—' },
          { label: 'Method', width: 100,
            render: r => (
              <Chip size="small" variant="outlined"
                label={(r.payment_method || '—').toUpperCase()}
                sx={{ height: 20, fontSize: 10, fontWeight: 700 }} />
            ) },
          { label: 'Cheque / Receipt', width: 140,
            cellSx: { fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' },
            render: r => r.cheque_no || r.receipt_no || '—' },
          { label: 'Narration', cellSx: { color: 'text.secondary' },
            render: r => r.narration || '—' },
          { label: 'Amount (GH₵)', align: 'right', width: 130,
            cellSx: { fontWeight: 700, color: 'success.main', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
            render: r => fmt(r.amount) },
          { label: '', align: 'right', width: 80,
            render: r => (
              <RowActions
                onEdit={canWriteIncome ? () => { setExpanded(null); openReceiptEdit(r); } : undefined}
                onDelete={canWriteIncome ? () => deleteReceipt(r) : undefined}
              />
            ) },
        ]}
      />

      <RecordsDialog
        open={expanded === 'bank'}
        onClose={() => setExpanded(null)}
        icon={<AccountBalance />} iconBg="primary.main"
        title="Cash & Bank" subtitle={`Balances and deposits for ${monthYear}`}
        total={cashInHand + totalBank} totalLabel="Total" totalColor="primary.main"
        rows={deposits.filter(d => (d.narration || '').trim().toUpperCase() !== 'BANK TO BANK')}
        searchOf={d => [d.account_name, d.narration].join(' ')}
        emptyIcon={<SwapHoriz />}
        emptyTitle="No deposits this month"
        emptyHint={canWriteIncome ? 'Move cash into a bank with "Cash → Bank".' : undefined}
        extra={
          <Box sx={{ flexShrink: 0 }}>
            <SubHeader label="Balances at close" count={(summary?.bank_balances?.length || 0) + 1} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {['Account', 'Brought Forward', 'Deposits In', 'Payments Out', 'Balance'].map((h, i) => (
                      <TableCell key={h} align={i === 0 ? 'left' : 'right'}
                        sx={{
                          fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap',
                          textTransform: 'uppercase', letterSpacing: '0.06em', color: 'text.secondary',
                        }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow hover sx={{ bgcolor: 'rgba(200,146,17,0.04)' }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1.25}>
                        <Savings sx={{ fontSize: 18, color: 'secondary.main' }} />
                        <Typography variant="body2" fontWeight={700}>Cash in Hand</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(summary?.cash_bfwd)}</TableCell>
                    <TableCell align="right" sx={{ color: 'success.main', fontVariantNumeric: 'tabular-nums' }}>
                      +{fmt(summary?.cash_income_this_month)}
                    </TableCell>
                    <TableCell align="right" sx={{ color: 'error.main', fontVariantNumeric: 'tabular-nums' }}>
                      −{fmt(summary?.cash_deposited_this_month)}
                    </TableCell>
                    <TableCell align="right" sx={{
                      fontWeight: 800, fontVariantNumeric: 'tabular-nums',
                      color: cashInHand >= 0 ? 'primary.main' : 'error.main',
                    }}>
                      {fmt(cashInHand)}
                    </TableCell>
                  </TableRow>
                  {(summary?.bank_balances || []).map(b => (
                    <TableRow key={b.id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1.25}>
                          <AccountBalance sx={{ fontSize: 18, color: 'primary.main' }} />
                          <Typography variant="body2" fontWeight={600}>{b.name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmt(b.bfwd)}</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main', fontVariantNumeric: 'tabular-nums' }}>
                        +{fmt(b.total_deposit)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'error.main', fontVariantNumeric: 'tabular-nums' }}>
                        −{fmt(b.total_payment)}
                      </TableCell>
                      <TableCell align="right" sx={{
                        fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                        color: Number(b.balance) >= 0 ? 'primary.main' : 'error.main',
                      }}>
                        {fmt(b.balance)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow sx={{ bgcolor: '#F1F5F9' }}>
                    <TableCell colSpan={4} sx={{ fontWeight: 800 }}>Total (Cash + Bank)</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 800, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}>
                      {fmt(cashInHand + totalBank)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
            <SubHeader
              label="Cash → Bank Deposits"
              count={deposits.filter(d => (d.narration || '').trim().toUpperCase() !== 'BANK TO BANK').length}
            />
          </Box>
        }
        columns={[
          { label: 'Date', width: 96, cellSx: { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
            render: d => shortDate(d.deposit_date) },
          { label: 'Deposited Into', cellSx: { fontWeight: 600 },
            render: d => (
              <Box display="flex" alignItems="center" gap={1}>
                <SwapHoriz sx={{ fontSize: 16, color: 'text.disabled' }} />
                {d.account_name || '—'}
              </Box>
            ) },
          { label: 'Narration', cellSx: { color: 'text.secondary' },
            render: d => d.narration || '—' },
          { label: 'Amount (GH₵)', align: 'right', width: 130,
            cellSx: { fontWeight: 700, color: 'primary.main', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
            render: d => fmt(d.amount) },
          { label: '', align: 'right', width: 80,
            render: d => (
              <RowActions
                onEdit={canWriteIncome ? () => { setExpanded(null); openDepositEdit(d); } : undefined}
                onDelete={canWriteIncome ? () => deleteDeposit(d) : undefined}
              />
            ) },
        ]}
      />

      <RecordsDialog
        open={expanded === 'payments'}
        onClose={() => { setExpanded(null); setChequeFilter(''); }}
        icon={<PaymentsIcon />} iconBg="error.main"
        title="Payments" subtitle={`All vouchers for ${monthYear}`}
        total={monthExpense} totalLabel="Paid" totalColor="error.main"
        rows={chequePayments}
        searchOf={p => [p.pv_number, p.expense_type, p.expense_category, p.account_name,
                        p.cheque_no, p.request_holder, p.description].join(' ')}
        toolbar={
          <TextField
            select size="small" label="Filter by cheque no"
            value={chequeFilter}
            onChange={e => setChequeFilter(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <ReceiptLong sx={{ fontSize: 17, color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value=""><em>All cheques</em></MenuItem>
            {chequeGroups.length === 0 && (
              <MenuItem value="" disabled>No cheque payments this month</MenuItem>
            )}
            {chequeGroups.map(g => (
              <MenuItem key={g.no} value={g.no}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                  <Box component="span" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{g.no}</Box>
                  <Chip
                    size="small"
                    label={`${g.count} voucher${g.count === 1 ? '' : 's'}`}
                    color={g.count > 1 ? 'secondary' : 'default'}
                    variant={g.count > 1 ? 'filled' : 'outlined'}
                    sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
                  />
                  <Box component="span" sx={{ ml: 'auto', fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
                    GH₵ {fmt(g.total)}
                  </Box>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        }
        banner={activeCheque && (
          <Box sx={{
            px: 2, py: 1.5, flexShrink: 0,
            bgcolor: 'rgba(200,146,17,0.08)',
            borderBottom: '1px solid #E2E8F0',
            display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap',
          }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" fontWeight={700}>
                Cheque <Box component="span" sx={{ fontFamily: 'monospace' }}>{activeCheque.no}</Box>
                {' — '}{activeCheque.count} voucher{activeCheque.count === 1 ? '' : 's'}
                {' · '}GH₵ {fmt(activeCheque.total)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {[...activeCheque.accounts].join(', ') || '—'}
                {' · '}Print them as one voucher, or use the voucher icon on a row to print it on its own.
              </Typography>
            </Box>
            <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
              <Button size="small" onClick={() => setChequeFilter('')}>Clear</Button>
              <Button
                size="small" variant="contained" startIcon={<ReceiptLong />}
                onClick={() => { setExpanded(null); raiseVoucher(chequePayments[0], 'cheque'); }}
                disabled={chequePayments.length === 0}
              >
                {activeCheque.count > 1 ? 'Print combined voucher' : 'Print voucher'}
              </Button>
            </Stack>
          </Box>
        )}
        emptyIcon={<InboxOutlined />}
        emptyTitle="No vouchers yet"
        emptyHint={canWritePayment ? 'Payment vouchers you record will appear here.' : undefined}
        columns={[
          { label: 'Date', width: 96, cellSx: { whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' },
            render: p => shortDate(p.payment_date) },
          { label: 'PV No', width: 110,
            cellSx: { fontFamily: 'monospace', fontSize: 12, color: 'text.secondary', whiteSpace: 'nowrap' },
            render: p => (p.pv_number || '').split('/').slice(-1)[0] || '—' },
          { label: 'Expense Type', cellSx: { fontWeight: 600 },
            render: p => (
              <Box display="flex" alignItems="center" gap={0.75}>
                {p.expense_type || (p.is_bank_to_bank ? 'Bank Transfer' : '—')}
                {!!p.is_bank_to_bank && (
                  <Chip size="small" label="BTB" variant="outlined" color="warning"
                    sx={{ height: 17, fontSize: 9, fontWeight: 700 }} />
                )}
              </Box>
            ) },
          { label: 'Category',
            render: p => p.is_bank_to_bank ? 'BANK-TO-BANK' : (p.expense_category || '—') },
          { label: 'Paid From', render: p => p.account_name || '—' },
          { label: 'Cheque No', width: 110,
            cellSx: { fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' },
            render: p => p.cheque_no || '—' },
          { label: 'Request Holder', render: p => p.request_holder || '—' },
          { label: 'Description', cellSx: { color: 'text.secondary' },
            render: p => p.description || '—' },
          { label: 'Amount (GH₵)', align: 'right', width: 130,
            cellSx: { fontWeight: 700, color: 'error.main', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
            render: p => fmt(p.amount) },
          { label: '', align: 'right', width: 110,
            render: p => (
              <RowActions
                onVoucher={() => { setExpanded(null); raiseVoucher(p, 'one'); }}
                onEdit={canWritePayment ? () => { setExpanded(null); openPaymentEdit(p); } : undefined}
                onDelete={canWritePayment ? () => deletePayment(p) : undefined}
              />
            ) },
        ]}
      />

      <PaymentVoucherDialog
        open={!!voucherFor}
        onClose={() => setVoucherFor(null)}
        payment={voucherFor}
        initialScope={voucherScope}
      />

      {/* Accounting years dialog */}
      <Dialog open={yearsOpen} onClose={() => setYearsOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{
          display: 'flex', alignItems: 'center', gap: 2,
          background: 'linear-gradient(90deg, #0B2545, #13315C)', color: '#fff', py: 2.5,
        }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 1.5, bgcolor: 'secondary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Settings />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Accounting Years</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
              Only active years appear in the year picker.
            </Typography>
          </Box>
          <IconButton onClick={() => setYearsOpen(false)} sx={{ color: '#fff' }}><Close /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
            <TextField size="small" label="Add year" type="number" value={newYear}
              onChange={e => setNewYear(e.target.value)}
              placeholder="e.g. 2027" sx={{ width: 200 }}
              onKeyDown={e => e.key === 'Enter' && addYear()} />
            <Button variant="contained" startIcon={<Add />} onClick={addYear}>Add</Button>
          </Stack>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Year</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {periods.length === 0 ? (
                <TableRow><TableCell colSpan={3} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                  No accounting years set up.
                </TableCell></TableRow>
              ) : periods.map(p => (
                <TableRow key={p.id} hover>
                  <TableCell><Typography variant="body2" fontWeight={700}>{p.year}</Typography></TableCell>
                  <TableCell>
                    <Chip label={p.is_active ? 'Active' : 'Inactive'}
                      color={p.is_active ? 'success' : 'default'}
                      size="small" sx={{ cursor: 'pointer' }}
                      onClick={() => toggleYear(p)} />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" color="error" onClick={() => removeYear(p)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, bgcolor: '#FAFBFC' }}>
          <Button onClick={() => setYearsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* Reusable section card wrapper — always fills its Grid item so columns stay flush */
function SectionCard({
  icon, iconBg = 'primary.main', title, subtitle,
  total, totalLabel, totalColor, count, onExpand, children,
}) {
  return (
    <Card sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{
        px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 2,
        borderBottom: '1px solid #E2E8F0', flexShrink: 0,
      }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 1.5, bgcolor: iconBg, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(11,37,69,0.12)',
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.15 }}>
            {title}
            {typeof count === 'number' && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75, fontWeight: 600 }}>
                · {count}
              </Typography>
            )}
          </Typography>
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        </Box>
        {total != null && (
          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 700, fontSize: 10 }}>
              {totalLabel}
            </Typography>
            <Typography variant="h6" fontWeight={800} color={totalColor} sx={{
              fontVariantNumeric: 'tabular-nums', lineHeight: 1.1,
            }}>
              GH₵ {fmt(total)}
            </Typography>
          </Box>
        )}
        {onExpand && (
          <Tooltip title={`Open ${title} in full view`}>
            <IconButton
              size="small" onClick={onExpand}
              sx={{
                flexShrink: 0, color: 'text.secondary',
                border: '1px solid #E2E8F0', borderRadius: 1.5,
                '&:hover': { color: 'primary.main', borderColor: 'primary.main', bgcolor: 'rgba(11,37,69,0.04)' },
              }}
            >
              <OpenInFull sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      {/* The one scroll region for this column. Everything below the card
          header lives in here, so scrolling a column never moves the page. */}
      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        overscrollBehavior: 'contain',   // don't hand the scroll back to the page
        scrollbarWidth: 'thin',
        scrollbarColor: '#CBD5E1 #F1F5F9',
        '&::-webkit-scrollbar':       { width: 8 },
        '&::-webkit-scrollbar-track': { backgroundColor: '#F1F5F9' },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#CBD5E1',
          borderRadius: 4,
          '&:hover': { backgroundColor: '#94A3B8' },
        },
      }}>
        {children}
      </Box>
    </Card>
  );
}

/* ─── Full-view dialog ──────────────────────────────────────────────────────
   Opens a column's records on a big screen as a proper detail table, with a
   search box and a sticky total. `columns` is [{ label, align, width, render }].
   `extra` renders above the main table (used for the balances table). */
function RecordsDialog({
  open, onClose, icon, iconBg = 'primary.main', title, subtitle,
  rows = [], columns = [], searchOf, total, totalLabel, totalColor,
  emptyIcon, emptyTitle, emptyHint, extra, toolbar, banner,
}) {
  const [q, setQ] = useState('');
  useEffect(() => { if (open) setQ(''); }, [open]);

  const needle = q.trim().toLowerCase();
  const filtered = !needle || !searchOf
    ? rows
    : rows.filter(r => String(searchOf(r) || '').toLowerCase().includes(needle));
  const filteredTotal = filtered.reduce((s, r) => s + Number(r.amount || 0), 0);
  const isFiltered = needle.length > 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 2, height: { xs: '100%', sm: '88vh' } } }}>
      <DialogTitle sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        background: 'linear-gradient(90deg, #0B2545, #13315C)', color: '#fff', py: 2.5,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 1.5, bgcolor: iconBg, color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
        }}>
          {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>{title}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>{subtitle}</Typography>
        </Box>
        {total != null && (
          <Box sx={{ textAlign: 'right', mr: 1 }}>
            <Typography variant="caption" sx={{
              color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase',
              letterSpacing: '0.12em', fontWeight: 700, fontSize: 10, display: 'block',
            }}>
              {totalLabel}
            </Typography>
            <Typography variant="h6" fontWeight={800} sx={{
              color: 'secondary.light', lineHeight: 1.1, fontVariantNumeric: 'tabular-nums',
            }}>
              GH₵ {fmt(total)}
            </Typography>
          </Box>
        )}
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {extra}

        {(searchOf || toolbar) && (
          <Box sx={{
            px: 2, py: 1.5, borderBottom: '1px solid #E2E8F0', flexShrink: 0,
            display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          }}>
            {searchOf && <TextField
              size="small" placeholder="Search these records…"
              value={q} onChange={e => setQ(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ fontSize: 18, color: 'text.disabled' }} />
                  </InputAdornment>
                ),
                endAdornment: q ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQ('')}><Close sx={{ fontSize: 16 }} /></IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{ flex: '1 1 240px', maxWidth: 400 }}
            />}
            {toolbar}
          </Box>
        )}

        {banner}

        {filtered.length === 0 ? (
          <EmptyState
            icon={isFiltered ? <Search /> : emptyIcon}
            title={isFiltered ? `Nothing matches "${q}"` : emptyTitle}
            hint={isFiltered ? 'Try a different search term.' : emptyHint}
          />
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map(c => (
                    <TableCell key={c.label} align={c.align || 'left'}
                      sx={{
                        width: c.width, whiteSpace: 'nowrap',
                        fontWeight: 700, fontSize: 11,
                        textTransform: 'uppercase', letterSpacing: '0.06em',
                        color: 'text.secondary', bgcolor: '#F8FAFC',
                      }}>
                      {c.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.map((r, i) => (
                  <TableRow key={r.id ?? i} hover>
                    {columns.map(c => (
                      <TableCell key={c.label} align={c.align || 'left'}
                        sx={{ fontSize: 13, ...(c.cellSx || {}) }}>
                        {c.render(r, i)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.75, bgcolor: '#FAFBFC', gap: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
          {isFiltered
            ? `Showing ${filtered.length} of ${rows.length} records`
            : `${rows.length} record${rows.length === 1 ? '' : 's'}`}
          {isFiltered && (
            <Box component="span" sx={{ ml: 1.5, fontWeight: 700, color: totalColor, fontVariantNumeric: 'tabular-nums' }}>
              GH₵ {fmt(filteredTotal)}
            </Box>
          )}
        </Typography>
        <Button onClick={onClose} variant="contained">Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/* Small edit/delete pair reused by the full-view tables */
function RowActions({ onVoucher, onEdit, onDelete }) {
  if (!onVoucher && !onEdit && !onDelete) return <Typography variant="caption" color="text.disabled">—</Typography>;
  return (
    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
      {onVoucher && (
        <Tooltip title="Raise payment voucher">
          <IconButton size="small" onClick={onVoucher} sx={{ p: 0.4, color: 'primary.main' }}>
            <ReceiptLong sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit} sx={{ p: 0.4 }}>
            <Edit sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={onDelete} sx={{ p: 0.4 }}>
            <Delete sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  );
}

/* Sub-header bar used inside a SectionCard to separate sub-sections */
function SubHeader({ label, count }) {
  return (
    // Sticky so the section label stays readable while its rows scroll past.
    <Box sx={{
      px: 2, py: 0.9,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      bgcolor: '#F8FAFC',
      borderTop: '1px solid #E2E8F0',
      borderBottom: '1px solid #E2E8F0',
      flexShrink: 0,
      position: 'sticky', top: 0, zIndex: 2,
    }}>
      <Typography variant="caption" fontWeight={700} color="text.secondary"
        sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 10 }}>
        {label}
      </Typography>
      {typeof count === 'number' && (
        <Typography variant="caption" fontWeight={700} color="text.disabled"
          sx={{ fontSize: 10, fontVariantNumeric: 'tabular-nums' }}>
          {count}
        </Typography>
      )}
    </Box>
  );
}

/* Empty state — centers itself in whatever space it's given */
function EmptyState({ icon, title, hint }) {
  return (
    <Box sx={{
      flex: 1, minHeight: 140,
      display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      color: 'text.secondary', px: 3, py: 3, textAlign: 'center',
    }}>
      {icon && (
        <Box sx={{
          mb: 1.5, color: '#CBD5E1',
          '& svg': { fontSize: 36 },
        }}>{icon}</Box>
      )}
      <Typography variant="body2" fontWeight={600} color="text.secondary">{title}</Typography>
      {hint && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, maxWidth: 220 }}>
          {hint}
        </Typography>
      )}
    </Box>
  );
}

/* Compact two-line row used inside narrow columns */
function FeedRow({ primary, secondary, tertiary, amount, amountColor, onVoucher, onEdit, onDelete }) {
  return (
    <Box sx={{
      px: 2, py: 1.1,
      display: 'flex', alignItems: 'center', gap: 1,
      borderBottom: '1px solid #F1F5F9',
      borderLeft: '3px solid transparent',
      transition: 'background-color 120ms, border-color 120ms',
      '&:last-child': { borderBottom: 'none' },
      '&:hover': {
        bgcolor: '#F8FAFC',
        borderLeftColor: amountColor,
      },
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" fontWeight={700} noWrap sx={{ color: 'text.primary' }}>
          {primary}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap display="block">
          {secondary}
        </Typography>
        {tertiary && (
          <Typography variant="caption" color="text.disabled" noWrap display="block" sx={{ fontStyle: 'italic' }}>
            {tertiary}
          </Typography>
        )}
      </Box>
      <Typography variant="body2" fontWeight={800} color={amountColor}
        sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
        GH₵ {Number(amount || 0).toLocaleString('en-GH', { minimumFractionDigits: 2 })}
      </Typography>
      {onVoucher && (
        <Tooltip title="Raise payment voucher">
          <IconButton size="small" onClick={onVoucher} sx={{ p: 0.3, color: 'primary.main' }}>
            <ReceiptLong sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      )}
      {onEdit && (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={onEdit} sx={{ p: 0.3 }}>
            <Edit sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
      {onDelete && (
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={onDelete} sx={{ p: 0.3 }}>
            <Delete sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}
